import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Send, Radio, Users, Clock, CalendarClock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PRESETS = [
  { title: "🪔 दीपावली की शुभकामनाएँ!", body: "आप एवं आपके परिवार को INDIAN-NEWS-21 की ओर से दीपावली की हार्दिक शुभकामनाएँ।", url: "/" },
  { title: "🇮🇳 गणतंत्र दिवस मुबारक!", body: "देश के हर नागरिक को गणतंत्र दिवस की हार्दिक शुभकामनाएँ। जय हिन्द!", url: "/" },
  { title: "🎉 नया साल मुबारक!", body: "नये वर्ष की शुरुआत हो सच्ची ख़बरों के साथ — INDIAN-NEWS-21", url: "/" },
  { title: "✦ आज का विशेष संपादकीय", body: "प्रधान संपादक JAKIR MOMIN की क़लम से — आज का चुनिंदा संपादकीय पढ़ें।", url: "/editor" },
  { title: "📰 आज का ई-पेपर तैयार है", body: "आज का पूरा अख़बार PDF में डाउनलोड करें।", url: "/" },
];

export default function PushSenderPage() {
  const [form, setForm] = useState({ title: "", body: "", url: "/" });
  const [stats, setStats] = useState({ active: 0, total: 0 });
  const [history, setHistory] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/admin/push/stats"),
      api.get("/admin/push/history"),
      api.get("/admin/push/scheduled"),
    ]).then(([s, h, sch]) => {
      setStats(s.data); setHistory(h.data); setScheduled(sch.data);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title.trim()) { toast.error("शीर्षक ज़रूरी है"); return; }
    if (scheduleMode) {
      if (!scheduledFor) { toast.error("भेजने का समय चुनें"); return; }
      const when = new Date(scheduledFor);
      if (when.getTime() < Date.now() - 30 * 1000) {
        toast.error("समय भविष्य में होना चाहिए");
        return;
      }
      setSending(true);
      try {
        await api.post("/admin/push/schedule", { ...form, scheduled_for: scheduledFor });
        toast.success(`अनुसूचित: ${when.toLocaleString("hi-IN")}`);
        setForm({ title: "", body: "", url: "/" });
        setScheduledFor("");
        setScheduleMode(false);
        load();
      } catch (e) { toast.error(formatError(e)); } finally { setSending(false); }
      return;
    }
    if (stats.active === 0) {
      if (!window.confirm("अभी 0 सक्रिय सब्सक्राइबर हैं। फिर भी भेजें?")) return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/push/broadcast", form);
      toast.success(`भेजा गया: ${data.sent}/${data.total}`);
      setForm({ title: "", body: "", url: "/" });
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setSending(false); }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm("इस अनुसूचित सूचना को रद्द करें?")) return;
    try {
      await api.delete(`/admin/push/scheduled/${id}`);
      toast.success("रद्द कर दी गयी");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-push-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A]">पुश सूचना भेजें</h1>
          <p className="text-sm text-slate-500 mt-1">त्यौहार की शुभकामनाएँ, विशेष संपादकीय — सब पाठकों को एक क्लिक में भेजें</p>
        </div>
        <div className="flex gap-2">
          <div className="px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 flex items-center gap-2">
            <Users className="w-4 h-4"/>
            <div><b>{stats.active}</b> सक्रिय / {stats.total} कुल</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-5 h-5 text-[#DC2626]"/>
            <h2 className="font-bold text-lg text-[#0F172A]">नयी सूचना लिखें</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-admin text-slate-600 mb-1 block">शीर्षक * <span className="text-slate-400">(अधिकतम 180 अक्षर)</span></label>
              <Input value={form.title} maxLength={180}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="जैसे: 🪔 दीपावली मुबारक!" data-testid="push-title"/>
            </div>
            <div>
              <label className="text-xs font-admin text-slate-600 mb-1 block">संदेश <span className="text-slate-400">(अधिकतम 220 अक्षर)</span></label>
              <Textarea value={form.body} maxLength={220} rows={3}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="सूचना का मुख्य संदेश..." data-testid="push-body"/>
              <div className="text-right text-[10px] text-slate-400 mt-1">{form.body.length}/220</div>
            </div>
            <div>
              <label className="text-xs font-admin text-slate-600 mb-1 block">क्लिक पर खुलने वाला लिंक</label>
              <Input value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="/ या /article/some-slug या /editor" data-testid="push-url"/>
            </div>

            {/* Live preview */}
            <div>
              <div className="text-xs font-admin text-slate-500 uppercase tracking-widest mb-2">पूर्वावलोकन</div>
              <div className="bg-slate-900 rounded-lg p-4 flex items-start gap-3 text-white shadow-inner">
                <img src="/logo.png" alt="" className="w-10 h-10 rounded bg-white/95 object-contain shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{form.title || "शीर्षक यहाँ दिखेगा"}</div>
                  <div className="text-xs text-slate-300 line-clamp-2 mt-1">{form.body || "संदेश यहाँ दिखेगा…"}</div>
                  <div className="text-[10px] text-slate-500 mt-1">INDIAN-NEWS-21 · अभी</div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 flex-wrap gap-3">
              <button type="button" onClick={() => setScheduleMode(!scheduleMode)}
                className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border ${
                  scheduleMode ? "bg-amber-50 border-amber-400 text-amber-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
                data-testid="schedule-toggle">
                <CalendarClock className="w-4 h-4"/>{scheduleMode ? "अभी भेजना है" : "बाद के लिए अनुसूचित करें"}
              </button>
              {scheduleMode && (
                <Input type="datetime-local" value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                  className="max-w-xs" data-testid="schedule-datetime"/>
              )}
              <Button onClick={send} disabled={sending}
                className={`${scheduleMode ? "bg-amber-600 hover:bg-amber-700" : "bg-[#DC2626] hover:bg-[#B91C1C]"} text-white font-bold`}
                data-testid="push-send-btn">
                {scheduleMode ? <CalendarClock className="w-4 h-4 mr-2"/> : <Send className="w-4 h-4 mr-2"/>}
                {sending ? "भेज रहे हैं…" : scheduleMode ? "अनुसूचित करें" : "सभी को भेजें"}
              </Button>
            </div>
          </div>
        </div>

        {/* Sidebar: presets + scheduled + history */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-bold text-[#0F172A] mb-3">तैयार टेम्पलेट्स</h3>
            <div className="space-y-2">
              {PRESETS.map((p, i) => (
                <button key={p.title} onClick={() => setForm(p)}
                  className="w-full text-left px-3 py-2 rounded-md border border-slate-200 hover:border-[#EA580C] hover:bg-orange-50 transition text-sm"
                  data-testid={`preset-${i}`}>
                  <div className="font-medium text-[#0F172A] truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 truncate">{p.body}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-lg p-5" data-testid="scheduled-panel">
            <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-600"/>अनुसूचित सूचनाएँ</h3>
            {scheduled.length === 0 ? (
              <div className="text-xs text-slate-400 italic">अभी कोई अनुसूचित सूचना नहीं</div>
            ) : (
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {scheduled.map((p) => (
                  <li key={p.id} className={`text-xs border-l-2 pl-2 py-1 pr-1 flex items-start gap-2 ${
                    p.status === "sent" ? "border-emerald-500" :
                    p.status === "cancelled" ? "border-slate-300 opacity-50" :
                    "border-amber-500"
                  }`} data-testid={`scheduled-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#0F172A] truncate">{p.title}</div>
                      <div className="text-slate-500 mt-0.5">
                        {new Date(p.scheduled_for).toLocaleString("hi-IN")}
                        {" · "}<span className={`font-medium uppercase text-[10px] ${
                          p.status === "sent" ? "text-emerald-700" :
                          p.status === "cancelled" ? "text-slate-500" :
                          "text-amber-700"
                        }`}>{p.status}</span>
                      </div>
                    </div>
                    {p.status === "scheduled" && (
                      <button onClick={() => cancelScheduled(p.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`cancel-scheduled-${p.id}`}>
                        <X className="w-3.5 h-3.5"/>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Clock className="w-4 h-4"/>हाल का इतिहास</h3>
            {history.length === 0 ? (
              <div className="text-xs text-slate-400 italic">अभी कोई सूचना नहीं भेजी गयी</div>
            ) : (
              <ul className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="text-xs border-l-2 border-[#EA580C] pl-2 py-1">
                    <div className="font-medium text-[#0F172A] line-clamp-1">{h.title}</div>
                    <div className="text-slate-500 mt-0.5">
                      {h.sent}/{h.total} भेजा · {new Date(h.created_at).toLocaleString("hi-IN")}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
